import { c as _c } from "react/compiler-runtime";
import { Dispatch, SetStateAction, useState, useEffect, useMemo } from 'react';
import { Placement, State, createPopper } from '@popperjs/core';
import { useMeasure } from '.';
export type { Placement as PopoutPlacement };
export type { State as PopoutState };
export interface IArrowConfiguration {
  /** Defines the size of the arrow */
  arrowSize?: number;
  /** Background color to be used by arrow */
  backgroundColor?: string;
  /** Border color to be used by arrow */
  borderColor?: string;
  /** Defines the size of the arrow border */
  borderSize?: number;
  /** Defines if the popout is enabled */
  enabled?: boolean;
  /** Defines the offset value for the arrow */
  offset?: number;
}
export interface IPopoutConfiguration {
  /** Defines if the popout is enabled */
  enabled?: boolean;
  /** Defines the configuration of the arrow */
  arrow?: IArrowConfiguration;
  /** Defines the offset value for the popout */
  offset?: number;
  /**
   * Known height for positioning calculations.
   * Applied to the popout element before Popper measures it,
   * ensuring correct flip behavior for async-rendered content.
   */
  height?: number;
  /**
   * Indicates if popout will prevent overflow
   * Note: This can cause issues when popout size is the same as the reference.
   */
  preventOverflow?: boolean;
}
const DEFAULT_ARROW_SIZE = 8;
const DEFAULT_PADDING = 12;
export const usePopout = (reference, placement, configuration) => {
  const $ = _c(29);
  const [popoutElement, setPopoutElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const [internalIsPositioned, setInternalIsPositioned] = useState(false);
  let t0;
  if ($[0] !== configuration) {
    t0 = configuration || {};
    $[0] = configuration;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    offset,
    enabled,
    height,
    preventOverflow: t1,
    arrow: arrowConfiguration
  } = t0;
  const preventOverflow = t1 === undefined ? true : t1;
  const [state, setState] = useState();
  const [ref, t2] = useMeasure();
  const {
    width
  } = t2;
  const renderPopout = enabled && reference != null && popoutElement != null && width !== 0;
  const isPositioned = enabled ? internalIsPositioned : false;
  let t3;
  let t4;
  if ($[2] !== ref || $[3] !== reference) {
    t3 = () => {
      reference && ref(reference);
    };
    t4 = [ref, reference];
    $[2] = ref;
    $[3] = reference;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== arrowConfiguration?.arrowSize || $[7] !== arrowElement || $[8] !== offset || $[9] !== placement || $[10] !== popoutElement || $[11] !== preventOverflow || $[12] !== reference || $[13] !== renderPopout) {
    t5 = () => {
      if (renderPopout) {
        const popoutEl = popoutElement as HTMLElement;
        const popperInstance = createPopper(reference as HTMLElement, popoutEl, {
          placement,
          strategy: "fixed",
          onFirstUpdate: state_0 => {
            setState(state_0 as State);
            setInternalIsPositioned(true);
            if (arrowElement) {
              arrowElement.setAttribute("class", "arrow");
              arrowElement.setAttribute("aria-hidden", "true");
            }
          },
          modifiers: [{
            name: "arrow",
            enabled: !!arrowElement,
            options: {
              element: arrowElement,
              padding: offset ? 0 : DEFAULT_PADDING
            }
          }, {
            name: "preventOverflow",
            enabled: preventOverflow
          }, {
            name: "offset",
            options: {
              offset: [0, arrowConfiguration?.arrowSize ? arrowConfiguration?.arrowSize + arrowConfiguration?.arrowSize / 2 : arrowElement ? DEFAULT_PADDING : DEFAULT_ARROW_SIZE]
            }
          }]
        });
        return () => {
          popperInstance.destroy();
          setInternalIsPositioned(false);
        };
      }
    };
    $[6] = arrowConfiguration?.arrowSize;
    $[7] = arrowElement;
    $[8] = offset;
    $[9] = placement;
    $[10] = popoutElement;
    $[11] = preventOverflow;
    $[12] = reference;
    $[13] = renderPopout;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = arrowConfiguration?.arrowSize;
  let t7;
  if ($[15] !== arrowElement || $[16] !== height || $[17] !== offset || $[18] !== placement || $[19] !== popoutElement || $[20] !== preventOverflow || $[21] !== ref || $[22] !== reference || $[23] !== renderPopout || $[24] !== t6) {
    t7 = [t6, arrowElement, height, offset, placement, popoutElement, preventOverflow, ref, reference, renderPopout];
    $[15] = arrowElement;
    $[16] = height;
    $[17] = offset;
    $[18] = placement;
    $[19] = popoutElement;
    $[20] = preventOverflow;
    $[21] = ref;
    $[22] = reference;
    $[23] = renderPopout;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  useEffect(t5, t7);
  let t8;
  if ($[26] !== isPositioned || $[27] !== state) {
    t8 = {
      setPopoutElement,
      setArrowElement,
      state,
      isPositioned
    };
    $[26] = isPositioned;
    $[27] = state;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
};